import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePreviewMode } from '../../contexts/PreviewModeContext';
import AuthModal from '../AuthModal/AuthModal';
import NotificationsModal from '../NotificationsModal/NotificationsModal';
import SearchResults from '../SearchResults/SearchResults';
import { useSearch } from '../../hooks/useSearch';
import { getUserNotifications } from '../../firebase/orders';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { getCategoryTree } from '../../firebase/categories';
import './Header.css';
import logoHeader from '../../assets/images/logos/JG Printing Logo_header.png';
import { getRoleLabel, isAdminPanelRole } from '../../utils/adminRoles';

function Header({ onLoginClick, onMessageClick }) {
    const { currentUser, logOut, userRole } = useAuth();
    const isPreview = usePreviewMode();
    const navigate = useNavigate();
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [showAccountDropdown, setShowAccountDropdown] = useState(false);
    const [showNotifications, setShowNotifications] = useState(false);
    const [showContacts, setShowContacts] = useState(false);
    const [notificationCount, setNotificationCount] = useState(0);
    const [chatUnreadCount, setChatUnreadCount] = useState(0);
    const [contactInfo, setContactInfo] = useState({
        storeName: 'JG Printing',
        storeAddress: '42 Balagtas Street Purok 4 Lower Bicutan Taguig City, Taguig, Philippines, 1632',
        contactNumber: '0992-946-7318',
        emailAddress: 'jgprinting2024@gmail.com',
        businessHours: '',
        logoUrl: '',
    });
    const [categoryTree, setCategoryTree] = useState([]);

    const isAdmin = isAdminPanelRole(userRole);
    const adminRoleLabel = getRoleLabel(userRole);

    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isSearchExpanded, setIsSearchExpanded] = useState(false);
    const contactsRef = useRef(null);

    const { query, setQuery, results, clearSearch } = useSearch();
    const [showDesktopResults, setShowDesktopResults] = useState(false);
    const [showMobileResults,  setShowMobileResults]  = useState(false);
    const desktopInputRef  = useRef(null);
    const mobileInputRef   = useRef(null);
    const desktopSearchRef = useRef(null);
    const mobileSearchRef  = useRef(null);

    // ── Hide-on-scroll / show-on-scroll-up ───────────────────────────────────
    const [headerVisible, setHeaderVisible] = useState(true);
    const lastScrollY = useRef(0);
    const ticking = useRef(false);

    useEffect(() => {
        const handleScroll = () => {
            if (!ticking.current) {
                window.requestAnimationFrame(() => {
                    const currentScrollY = window.scrollY;
                    if (currentScrollY <= 60) {
                        // Always show near the top
                        setHeaderVisible(true);
                    } else if (currentScrollY < lastScrollY.current) {
                        // Scrolling UP — show
                        setHeaderVisible(true);
                    } else if (currentScrollY > lastScrollY.current) {
                        // Scrolling DOWN — hide
                        setHeaderVisible(false);
                        // Close any open dropdowns when hiding
                        setShowContacts(false);
                        setShowAccountDropdown(false);
                    }
                    lastScrollY.current = currentScrollY;
                    ticking.current = false;
                });
                ticking.current = true;
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (!currentUser || isPreview) { setNotificationCount(0); return; }
        const loadCount = async () => {
            const { notifications } = await getUserNotifications(currentUser.uid);
            const unread = (notifications || []).filter(n => !n.read).length;
            setNotificationCount(unread);
        };
        loadCount();
    }, [currentUser, showNotifications, isPreview]);

    useEffect(() => {
        if (!currentUser || isAdmin || isPreview) { setChatUnreadCount(0); return; }
        const chatRef = doc(db, 'chats', currentUser.uid);
        const unsubscribe = onSnapshot(chatRef, (snap) => {
            setChatUnreadCount(snap.exists() ? (snap.data()?.unreadByCustomer || 0) : 0);
        }, () => { setChatUnreadCount(0); });
        return () => unsubscribe();
    }, [currentUser, isAdmin, isPreview]);

    const handleMessageUsClick = () => {
        if (isPreview) return;
        if (onMessageClick && typeof onMessageClick === 'function') onMessageClick();
    };

    const handleBackToAdmin = () => {
        sessionStorage.removeItem('adminPreviewMode');
        window.close();
        setTimeout(() => {
            window.location.href = '/admin';
        }, 300);
    };

    useEffect(() => {
        if (isSearchExpanded && mobileInputRef.current) mobileInputRef.current.focus();
    }, [isSearchExpanded]);

    useEffect(() => {
        document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [isMobileMenuOpen]);

    const accountRef = useRef(null);
    
    useEffect(() => {
        function handleClickOutside(e) {
            if (contactsRef.current && !contactsRef.current.contains(e.target)) setShowContacts(false);
            if (accountRef.current && !accountRef.current.contains(e.target)) setShowAccountDropdown(false);
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const ref = doc(db, 'settings', 'store');
        const applyData = (data) => {
            setContactInfo(prev => ({
                storeName:     data.storeName     || prev.storeName,
                storeAddress:  data.storeAddress  || prev.storeAddress,
                contactNumber: data.contactNumber || prev.contactNumber,
                emailAddress:  data.emailAddress  || prev.emailAddress,
                businessHours: data.businessHours || prev.businessHours,
                logoUrl:       data.logoUrl       || '',
            }));
        };
        const unsubscribe = onSnapshot(ref, (snap) => {
            if (!snap.exists()) return;
            applyData(snap.data() || {});
        }, async () => {
            try {
                const snap = await getDoc(ref);
                if (!snap.exists()) return;
                applyData(snap.data() || {});
            } catch (err) {
                console.error('Failed to load store settings:', err);
            }
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        let mounted = true;
        getCategoryTree({ activeOnly: true }).then((result) => {
            if (!mounted || result.error) return;
            setCategoryTree(result.tree);
        });
        return () => { mounted = false; };
    }, []);

    const logoSrc = contactInfo.logoUrl || logoHeader;
    const closeMobileMenu = () => setIsMobileMenuOpen(false);

    const handleDesktopQueryChange = (e) => { setQuery(e.target.value); setShowDesktopResults(true); };
    const handleMobileQueryChange  = (e) => { setQuery(e.target.value); setShowMobileResults(true); };
    const handleDesktopFocus = () => { if (query.trim().length >= 2) setShowDesktopResults(true); };
    const handleMobileFocus  = () => { if (query.trim().length >= 2) setShowMobileResults(true); };

    const handleDesktopKeyDown = (e) => {
        if (e.key === 'Enter' && results.length > 0) { navigate(results[0].path, results[0].state ? { state: results[0].state } : undefined); closeAllSearch(); }
        if (e.key === 'Escape') closeAllSearch();
    };
    const handleMobileKeyDown = (e) => {
        if (e.key === 'Enter' && results.length > 0) { navigate(results[0].path, results[0].state ? { state: results[0].state } : undefined); closeAllSearch(); }
        if (e.key === 'Escape') closeAllSearch();
    };

    const closeAllSearch = () => { setShowDesktopResults(false); setShowMobileResults(false); clearSearch(); };

    const handleLogout = async () => {
        const result = await logOut();
        if (result.error) console.error('Logout error:', result.error);
        else navigate('/');
        setShowAccountDropdown(false);
        closeMobileMenu();
    };

    const handlePersonalInformation = () => { navigate('/account/personal-information'); setShowAccountDropdown(false); closeMobileMenu(); };
    const handleSavedAddresses      = () => { navigate('/account/saved-address');         setShowAccountDropdown(false); closeMobileMenu(); };
    const handlePurchaseHistory     = () => { navigate('/account/purchase-history');      setShowAccountDropdown(false); closeMobileMenu(); };
    const handleEmailNotifications  = () => { navigate('/account/email-notifications');   setShowAccountDropdown(false); closeMobileMenu(); };

    return (
        <>
            {/* Admin banner */}
            {isAdmin && !isPreview && (
                <div className="header-admin-banner">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                    You're browsing as <strong>Admin</strong> — cart and order submission are disabled.
                </div>
            )}

            {/* ── Main header — hide/show on scroll ── */}
            <header className={`header${headerVisible ? ' header--visible' : ' header--hidden'}`}>
                <div className="header-top">

                    <div className="mobile-left-group">
                        <button className="hamburger-btn" onClick={() => setIsMobileMenuOpen(true)} aria-label="Open menu">
                            <HamburgerIcon />
                        </button>
                        <button className="nav-link mobile-search-toggle" onClick={() => setIsSearchExpanded(prev => !prev)} aria-label="Toggle search">
                            <SearchIcon />
                        </button>
                    </div>

                    {/* Logo */}
                    <Link to="/" className="header-logo">
                        <img src={logoSrc} alt="JG Printing Logo" className="logo-image" />
                    </Link>

                    {/* Desktop search */}
                    <div className={`header-search desktop-search${showDesktopResults && query.trim().length >= 2 ? ' sr-open' : ''}`} ref={desktopSearchRef}>
                        <input
                            ref={desktopInputRef}
                            type="text"
                            placeholder="Search for product or service"
                            className="search-input"
                            value={query}
                            onChange={handleDesktopQueryChange}
                            onFocus={handleDesktopFocus}
                            onKeyDown={handleDesktopKeyDown}
                            autoComplete="off"
                        />
                        {query ? (
                            <button className="search-button search-clear-btn" aria-label="Clear search" onClick={closeAllSearch}><ClearIcon /></button>
                        ) : (
                            <button className="search-button" aria-label="Search"><SearchIcon /></button>
                        )}
                        {showDesktopResults && query.trim().length >= 2 && (
                            <SearchResults results={results} query={query} onClose={closeAllSearch} inputRef={desktopInputRef} />
                        )}
                    </div>

                    {/* Desktop nav */}
                    <nav className="header-nav desktop-nav">
                        {isAdmin && isPreview && (
                            <button className="nav-link back-to-admin-btn" onClick={handleBackToAdmin} title={`Back to ${adminRoleLabel}`}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M19 12H5M5 12L12 19M5 12L12 5"/>
                                </svg>
                                <span>Back to Admin</span>
                            </button>
                        )}

                        {!isPreview && (
                            <button className="nav-link message-us-btn" onClick={handleMessageUsClick}>
                                <span className="message-us-icon-wrap">
                                    <MessageIcon />
                                    {chatUnreadCount > 0 && <span className="message-us-unread-badge">{chatUnreadCount}</span>}
                                </span>
                                <span>Message Us</span>
                            </button>
                        )}

                        <div className="contacts-wrapper" ref={contactsRef}>
                            <button className="nav-link" onClick={() => setShowContacts(prev => !prev)}>
                                <ContactsIcon /><span>Contacts</span>
                            </button>
                            {showContacts && (
                                <div className="contacts-modal">
                                    <p><strong>Store:</strong> {contactInfo.storeName}</p>
                                    <p><strong>Address:</strong> {contactInfo.storeAddress}</p>
                                    <p><strong>Phone:</strong> {contactInfo.contactNumber}</p>
                                    <p><strong>Email:</strong> {contactInfo.emailAddress}</p>
                                    {contactInfo.businessHours && (
                                        <p><strong>Business Hours:</strong> {contactInfo.businessHours}</p>
                                    )}
                                </div>
                            )}
                        </div>

                        {currentUser && !isAdmin && !isPreview && (
                            <button className="nav-link notifications-btn" onClick={() => setShowNotifications(true)}>
                                <NotificationIcon /><span>Notifications</span>
                                {notificationCount > 0 && <span className="notification-badge-count">{notificationCount}</span>}
                            </button>
                        )}

                        {!isPreview && (
                            <>
                                {currentUser ? (
                                    <>
                                        <div className="account-settings" ref={accountRef}>
                                            <button onClick={() => setShowAccountDropdown(!showAccountDropdown)} className="nav-link account-btn">
                                                <UserIcon /><span>Account</span>
                                            </button>
                                            {showAccountDropdown && (
                                                <div className="dropdown-menu">
                                                    <button onClick={handlePersonalInformation}>Personal Info</button>
                                                    <button onClick={handleSavedAddresses}>Saved Address</button>
                                                    {!isAdmin && <button onClick={handlePurchaseHistory}>Purchase History</button>}
                                                    <button onClick={handleEmailNotifications}>Email Notification</button>
                                                    <button onClick={handleLogout}>Log out</button>
                                                </div>
                                            )}
                                        </div>
                                        {!isAdmin && (
                                            <Link to="/cart" className="nav-link">
                                                <CartIcon /><span>Cart</span>
                                            </Link>
                                        )}
                                    </>
                                ) : (
                                    <button className="nav-link login-button" onClick={() => setShowAuthModal(true)}>
                                        <UserIcon /><span>Log in</span>
                                    </button>
                                )}
                            </>
                        )}
                    </nav>

                    {/* Mobile icon strip */}
                    <div className="mobile-nav-icons">
                        {isAdmin && isPreview && (
                            <button className="nav-link back-to-admin-btn" onClick={handleBackToAdmin} aria-label={`Back to ${adminRoleLabel}`}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M19 12H5M5 12L12 19M5 12L12 5"/>
                                </svg>
                            </button>
                        )}

                        {!isPreview && (
                            <button className="nav-link message-us-btn" onClick={handleMessageUsClick} aria-label="Message Us">
                                <span className="message-us-icon-wrap">
                                    <MessageIcon />
                                    {chatUnreadCount > 0 && <span className="message-us-unread-badge">{chatUnreadCount}</span>}
                                </span>
                            </button>
                        )}

                        {currentUser && !isAdmin && !isPreview && (
                            <button className="nav-link notifications-btn" onClick={() => setShowNotifications(true)} aria-label="Notifications">
                                <NotificationIcon />
                                {notificationCount > 0 && <span className="notification-badge-count">{notificationCount}</span>}
                            </button>
                        )}

                        {!currentUser && !isPreview && (
                            <button className="nav-link" onClick={() => setShowAuthModal(true)} aria-label="Log in">
                                <UserIcon />
                            </button>
                        )}

                        {currentUser && !isAdmin && !isPreview && (
                            <Link to="/cart" className="nav-link" aria-label="Cart">
                                <CartIcon />
                            </Link>
                        )}
                    </div>
                </div>

                {/* Mobile expandable search */}
                <div className={`mobile-search-bar${isSearchExpanded ? ' expanded' : ''}`}>
                    <div className="mobile-search-inner" ref={mobileSearchRef} style={{ position: 'relative' }}>
                        <input
                            ref={mobileInputRef}
                            type="text"
                            placeholder="Search for product or service"
                            className="search-input"
                            value={query}
                            onChange={handleMobileQueryChange}
                            onFocus={handleMobileFocus}
                            onKeyDown={handleMobileKeyDown}
                            autoComplete="off"
                        />
                        {query ? (
                            <button className="search-button search-clear-btn" aria-label="Clear" onClick={closeAllSearch}><ClearIcon /></button>
                        ) : (
                            <button className="search-button" aria-label="Search"><SearchIcon /></button>
                        )}
                        {showMobileResults && query.trim().length >= 2 && (
                            <SearchResults results={results} query={query} onClose={closeAllSearch} inputRef={mobileInputRef} />
                        )}
                    </div>
                </div>

                {/* Desktop category bar */}
                <nav className="header-categories">
                    <div className="header-categories-inner">
                        <NavLink to="/" end className={({ isActive }) => `category-link${isActive ? ' active' : ''}`}>Home</NavLink>
                        {categoryTree.map((category) => (
                            <NavLink
                                key={category.id}
                                to={`/catalog?categoryId=${encodeURIComponent(category.id)}`}
                                className={({ isActive }) => `category-link${isActive ? ' active' : ''}`}
                            >
                                {category.name}
                            </NavLink>
                        ))}
                        <NavLink to="/promotions"  className={({ isActive }) => `category-link${isActive ? ' active' : ''}`}>Promotions</NavLink>
                        <NavLink to="/collections" className={({ isActive }) => `category-link${isActive ? ' active' : ''}`}>Collections</NavLink>
                    </div>
                </nav>
            </header>

            {/* Mobile Drawer */}
            {isMobileMenuOpen && <div className="drawer-overlay" onClick={closeMobileMenu} />}
            <aside className={`mobile-drawer${isMobileMenuOpen ? ' open' : ''}`}>
                <div className="drawer-header">
                    <Link to="/" className="header-logo" onClick={closeMobileMenu}>
                        <img src={logoSrc} alt="JG Printing Logo" className="logo-image" />
                    </Link>
                    <button className="drawer-close-btn" onClick={closeMobileMenu} aria-label="Close menu"><CloseIcon /></button>
                </div>
                <div className="drawer-content">
                    <div className="drawer-section">
                        <p className="drawer-section-title">Categories</p>
                        {categoryTree.map((category) => (
                            <Link
                                key={category.id}
                                to={`/catalog?categoryId=${encodeURIComponent(category.id)}`}
                                className="drawer-link"
                                onClick={closeMobileMenu}
                            >
                                {category.name}
                            </Link>
                        ))}
                        <Link to="/promotions"            className="drawer-link" onClick={closeMobileMenu}>Promotions</Link>
                        <Link to="/collections"           className="drawer-link" onClick={closeMobileMenu}>Collections</Link>
                    </div>
                    <div className="drawer-divider" />
                    <div className="drawer-section">
                        <p className="drawer-section-title">Help & Account</p>

                        {isAdmin && isPreview && (
                            <button className="drawer-link drawer-btn back-to-admin-drawer" onClick={handleBackToAdmin}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M19 12H5M5 12L12 19M5 12L12 5"/>
                                </svg>
                                Back to Admin
                            </button>
                        )}

                        {!isPreview && (
                            <button className="drawer-link drawer-btn" onClick={() => { closeMobileMenu(); handleMessageUsClick(); }}>
                                <span className="message-us-icon-wrap" style={{ position: 'relative' }}>
                                    <MessageIcon />
                                    {chatUnreadCount > 0 && <span className="message-us-unread-badge">{chatUnreadCount}</span>}
                                </span>
                                Message Us
                            </button>
                        )}

                        <div className="drawer-contacts-wrapper">
                            <button
                                type="button"
                                className="drawer-link drawer-btn"
                                onClick={() => setShowContacts(prev => !prev)}
                                aria-expanded={showContacts}
                                aria-haspopup="true"
                            >
                                <ContactsIcon />
                                Contacts
                            </button>
                            {showContacts && (
                                <div className="drawer-contacts-modal">
                                    <p><strong>Store:</strong> {contactInfo.storeName}</p>
                                    <p><strong>Address:</strong> {contactInfo.storeAddress}</p>
                                    <p><strong>Phone:</strong> {contactInfo.contactNumber}</p>
                                    <p><strong>Email:</strong> {contactInfo.emailAddress}</p>
                                    {contactInfo.businessHours && (
                                        <p><strong>Business Hours:</strong> {contactInfo.businessHours}</p>
                                    )}
                                </div>
                            )}
                        </div>

                        {!isPreview && (
                            <>
                                {currentUser ? (
                                    <>
                                        <button className="drawer-link drawer-btn" onClick={handlePersonalInformation}><UserIcon /> Personal Info</button>
                                        <button className="drawer-link drawer-btn" onClick={handleSavedAddresses}><UserIcon /> Saved Address</button>
                                        {!isAdmin && <button className="drawer-link drawer-btn" onClick={handlePurchaseHistory}><UserIcon /> Purchase History</button>}
                                        <button className="drawer-link drawer-btn" onClick={handleEmailNotifications}><UserIcon /> Email Notifications</button>
                                        <button className="drawer-link drawer-btn drawer-logout" onClick={handleLogout}>Log out</button>
                                    </>
                                ) : (
                                    <button className="drawer-link drawer-btn" onClick={() => { closeMobileMenu(); setShowAuthModal(true); }}>
                                        <UserIcon /> Log in
                                    </button>
                                )}
                            </>
                        )}

                        {isPreview && (
                            <div style={{ fontSize: '12px', color: '#888', padding: '10px 0', fontStyle: 'italic', lineHeight: '1.5' }}>
                                Account features are disabled in Admin Preview Mode.
                            </div>
                        )}
                    </div>
                </div>
            </aside>

            {!isPreview && <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />}
            {!isAdmin && !isPreview && <NotificationsModal isOpen={showNotifications} onClose={() => setShowNotifications(false)} />}
        </>
    );
}

/* ── Icons ── */
function HamburgerIcon()    { return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>; }
function CloseIcon()        { return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>; }
function NotificationIcon() { return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>; }
function SearchIcon()       { return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>; }
function ClearIcon()        { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>; }
function MessageIcon()      { return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>; }
function ContactsIcon()     { return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/></svg>; }
function UserIcon()         { return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>; }
function CartIcon()         { return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/></svg>; }

export default Header;
